<template>
  <div class="home" ref="homeContainer">
    <!-- 모바일: 기존 이미지 + CTA -->
    <MobileImageSection v-if="isMobile" @open-form="$emit('open-form')" />

    <!-- PC: 풀스크린 섹션들 -->
    <template v-else>
      <HeroSection ref="section0" />
      <IntroSection ref="section1" />
      <CommunitySection ref="section2" />
      <VideoSection ref="section3" />
      <LocationSection ref="section4" />
      <FooterSection ref="section5" @open-form="$emit('open-form')" />
    </template>
  </div>
</template>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import MobileImageSection from '@/components/mobile/MobileImageSection.vue'
import HeroSection from '@/components/home/HeroSection.vue'
import IntroSection from '@/components/home/IntroSection.vue'
import CommunitySection from '@/components/home/CommunitySection.vue'
import VideoSection from '@/components/home/VideoSection.vue'
import LocationSection from '@/components/home/LocationSection.vue'
import FooterSection from '@/components/home/FooterSection.vue'

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

export default {
  name: 'HomeView',
  components: {
    MobileImageSection,
    HeroSection,
    IntroSection,
    CommunitySection,
    VideoSection,
    LocationSection,
    FooterSection
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentSection: 0,
      isScrolling: false,
      sections: [],
      scrollTimeout: null
    }
  },
  emits: ['open-form'],
  mounted() {
    if (!this.isMobile) {
      this.$nextTick(() => {
        this.sections = [
          this.$refs.section0.getSection(),     // 0: Hero
          this.$refs.section1.getSection(),     // 1: Intro
          this.$refs.section2.getSection(),     // 2: Community
          this.$refs.section3.getSection(),     // 3: Video
          this.$refs.section4.getSection(),     // 4: Location
          this.$refs.section5.getSection()      // 5: Footer
        ]
        this.initFullpageScroll()
      })
    }
  },
  beforeUnmount() {
    ScrollTrigger.getAll().forEach(trigger => trigger.kill())
    window.removeEventListener('wheel', this.handleWheel)
    window.removeEventListener('keydown', this.handleKeydown)
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }
  },
  methods: {
    initFullpageScroll() {
      window.addEventListener('wheel', this.handleWheel, { passive: false })
      window.addEventListener('keydown', this.handleKeydown)
    },
    handleWheel(e) {
      e.preventDefault()

      if (this.isScrolling) return

      // 스크롤 민감도 조절 - 작은 스크롤은 무시
      if (Math.abs(e.deltaY) < 20) return

      const direction = e.deltaY > 0 ? 1 : -1

      this.handleScrollDirection(direction)
    },
    handleScrollDirection(direction) {
      // IntroSection (section1)에서는 내부 피처 스크롤 처리
      if (this.currentSection === 1) {
        const introSection = this.$refs.section1
        if (introSection && introSection.handleScroll) {
          const handled = introSection.handleScroll(direction)
          if (handled) {
            this.isScrolling = true
            setTimeout(() => {
              this.isScrolling = false
            }, 700)
            return
          }
        }
      }

      // CommunitySection (section2)에서는 내부 커뮤니티 스크롤 처리
      if (this.currentSection === 2) {
        const communitySection = this.$refs.section2
        if (communitySection && communitySection.handleScroll) {
          const handled = communitySection.handleScroll(direction)
          if (handled) {
            this.isScrolling = true
            setTimeout(() => {
              this.isScrolling = false
            }, 700)
            return
          }
        }
      }

      const nextSection = this.currentSection + direction

      if (nextSection >= 0 && nextSection < this.sections.length) {
        // IntroSection으로 진입 시 피처 상태 설정
        if (nextSection === 1) {
          const introSection = this.$refs.section1
          if (introSection) {
            if (direction > 0) {
              introSection.resetToFirst()
            } else {
              introSection.setToLast()
            }
          }
        }
        // CommunitySection으로 진입 시 상태 설정
        if (nextSection === 2) {
          const communitySection = this.$refs.section2
          if (communitySection) {
            if (direction > 0) {
              communitySection.resetToFirst()
            } else {
              communitySection.setToLast()
            }
          }
        }
        // VideoSection으로 진입 시 상태 설정
        if (nextSection === 3) {
          const videoSection = this.$refs.section3
          if (videoSection) {
            if (direction > 0) {
              videoSection.resetToFirst()
            } else {
              videoSection.setToLast()
            }
          }
        }
        this.goToSection(nextSection)
      } else if (nextSection < 0 || nextSection >= this.sections.length) {
        return
      }
    },
    handleKeydown(e) {
      if (this.isScrolling) return

      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault()
        this.handleScrollDirection(1)
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault()
        this.handleScrollDirection(-1)
      }
    },
    goToSection(index) {
      this.isScrolling = true
      this.currentSection = index

      // 기존 타임아웃 클리어
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout)
      }

      gsap.to(window, {
        duration: 0.8,
        scrollTo: {
          y: this.sections[index],
          offsetY: 0
        },
        ease: 'power2.inOut',
        onComplete: () => {
          // 약간의 딜레이 후 스크롤 가능하게
          this.scrollTimeout = setTimeout(() => {
            this.isScrolling = false
          }, 200)
        }
      })
    }
  }
}
</script>

<style scoped>
.home {
  width: 100%;
}
</style>
